import type {PayloadStrategy} from '@/pages/components/audit/adjustment/adapters/types';
import {AdjustDialogKey} from "@/adjustment/adjust/dialogs/utils/paper-registry";
import {
    basicStrategy,
    commonMaterialTestStrategy,
    commonStrategy,
    commonTransactionStrategy,
    bankStrategy, debtFeesTransactionStrategy, InvestmentSubsidiaryStrategy
} from '@/pages/components/audit/adjustment/adapters/strategies/common-str';
import {bankLoanStrategy, vehicleLoanStrategy} from '@/pages/components/audit/adjustment/adapters/strategies/loan-str';
import {
    rentalExpensesStrategy,
    donationStrategy,
    commissionPaymentStrategy,
    withholdingTaxStrategy,
    purchasesStrategy, salariesAllowanceStrategy,
    salariesWageStrategy, directorSalaryStrategy, mpfSummaryStrategy, interestSummaryStrategy,
} from '@/pages/components/audit/adjustment/adapters/strategies/expense-str';
import {
    inventoriesStrategy,
    InvestmentInTaxiCabsAndLicensesStrategy,
    propertyPlantEquipmentStrategy,
    depreciationSharedStrategy,
    intangibleAssetsStrategy,
} from "@/adjustment/adapters/strategies/asset-str";

import {
    dueFromToDirectorsStrategy, gainLossDisposalStrategy,
    gainLossExchangeStrategy
} from "@/adjustment/adapters/strategies/dynamic-str";
import {
    interestIncomeStrategy, salesIncomeStrategy
} from "@/adjustment/adapters/strategies/income-str";

// Compose a map of strategies keyed by dialog key names
export const strategyMap: Record<AdjustDialogKey, PayloadStrategy> = {

    BASIC_ADJUST: basicStrategy,
    SALES_INCOME_ADJUST: salesIncomeStrategy,
    // LOAN
    BANK_LOAN: bankLoanStrategy,
    VEHICLE_LOAN: vehicleLoanStrategy,
    NON_BANK_LOAN: bankLoanStrategy,
    NON_VEHICLE_LOAN: vehicleLoanStrategy,

    // dynamic
    DUE_FROM_TO_DIRECTORS: dueFromToDirectorsStrategy,
    GAIN_LOSS_EXCHANGE: gainLossExchangeStrategy,
    GAIN_LOSS_DISPOSAL: gainLossDisposalStrategy,

    // Asset - E session
    INTANGIBLE_ASSETS: intangibleAssetsStrategy,

    // Asset - H session
    PROPERTY_PLANT_EQUIPMENT: propertyPlantEquipmentStrategy,

    // Asset - I session
    INVENTORIES: inventoriesStrategy,

    // Asset - G session
    INVESTMENT_IN_TAXI_CABS_AND_LICENSES: InvestmentInTaxiCabsAndLicensesStrategy,
    INVESTMENT_IN_SUBSIDIARY: InvestmentSubsidiaryStrategy,
    INVESTMENT_IN_ASSOCIATE: InvestmentSubsidiaryStrategy,
    INVESTORIES: inventoriesStrategy,
    INVESTMENT_ON_TRUSTS: InvestmentSubsidiaryStrategy,

    // Asset - J session
    OTHER_RECEIVABLES: commonStrategy,
    DEPOSIT_PAID: commonStrategy,
    PREPAYMENTS: commonStrategy,
    TRADE_RECEIVABLES: commonStrategy,
    DUE_FROM_IMMEDIATE_HOLDING_COMPANY: commonStrategy,
    DUE_FROM_SOLE_SHAREHOLDER: commonStrategy,
    DUE_FROM_RELATED_PARTIES: commonStrategy,
    DUE_FROM_RELATED_PARTIES_2: commonStrategy,
    DUE_FROM_RELATED_PARTIES_3: commonStrategy,

    // Liabilities - L Session
    TRADE_PAYABLES: commonStrategy,
    OTHER_PAYABLES: commonStrategy,
    DEPOSIT_RECEIVED: commonStrategy,
    ACCRUED_EXPENSES: commonStrategy,
    BANK_OVER_DRAFT: bankStrategy,
    DUE_TO_SUBSIDIARY: commonStrategy,
    DUE_TO_SHAREHOLDER: commonStrategy,
    DUE_TO_RELATED_COMPANY: commonStrategy,
    DUE_TO_RELATED_PARTIES: commonStrategy,
    DUE_TO_RELATED_PARTIES_2: commonStrategy,
    DUE_TO_RELATED_PARTIES_3: commonStrategy,
    OTHER_NON_CURRENT_ITEMS: commonStrategy,
    OTHER_FUND_FROM_SHAREHOLDER: commonStrategy,

    // Equity - K
    BANK_BALANCE_AND_CASH: bankStrategy,

    // Income - QI
    SALES_INCOME: salesIncomeStrategy,
    SERVICE_INCOME: salesIncomeStrategy,
    INTEREST_INCOME: interestIncomeStrategy,
    SUBSIDIES_FROM_GOVERNMENT: interestIncomeStrategy,
    DIVIDEND_INCOME: commonTransactionStrategy,
    ALLOWANCE_INCOME: commonTransactionStrategy,
    SUNDRY_INCOME: commonTransactionStrategy,

    // Expenses - QE session
    PURCHASES: purchasesStrategy,
    SALARIES_ALLOWANCE: salariesAllowanceStrategy,
    ROYALTY_EXPENSES: commonTransactionStrategy,
    OTHER_COST_OF_TURNOVER: commonTransactionStrategy,
    
    // Cost of Turnover codes (PL-COT)
    'PL-COT-ROY': commonTransactionStrategy,
    'PL-COT-SUB': debtFeesTransactionStrategy,
    'PL-COT-OCT': commonTransactionStrategy,
    /** Company custom Level-3 nodes (paper-registry templateCodeRef). */
    CommonTransactionWp: commonTransactionStrategy,

    // Expenses - R session
    ACCOUNTING_FEE: commonMaterialTestStrategy,//R1
    ADMISSION_FEE: commonMaterialTestStrategy,//R2
    ADVERTISING_AND_PROMOTION: commonMaterialTestStrategy,//R3
    ANNUAL_RETURN: commonMaterialTestStrategy,//R4
    AUDIT_AND_OTHER_PROFESSIONAL_FEE: commonMaterialTestStrategy,//R5
    BANK_CHARGES: commonMaterialTestStrategy,//R6
    BUILDING_MANAGEMENT_FEE: commonMaterialTestStrategy,//R7
    BUSINESS_REGISTRATION_FEE: commonMaterialTestStrategy,//R8
    CERTIFICATION_FEE: commonMaterialTestStrategy,//R9
    CLEANING_EXPENSES: commonMaterialTestStrategy,//R10

    COMPUTER_EXPENSES: commonMaterialTestStrategy,//R11
    COMPUTER_SYSTEM_EXPENSES: commonMaterialTestStrategy,//R12
    CONSUMABLE: commonMaterialTestStrategy,//R13
    WITHHOLDING_TAX: withholdingTaxStrategy,//R14 (Tax P1.1 (2))
    COURIER_AND_POSTAGE: commonMaterialTestStrategy,//R15
    CROWN_RENT_AND_RATES: commonMaterialTestStrategy,//R16
    CUSTOMS_AND_DECLARATION: commonMaterialTestStrategy,//R17
    DESIGN_FEE: commonMaterialTestStrategy,//R18
    ELECTRICITY_AND_WATER: commonMaterialTestStrategy,//R19
    ENTERTAINMENT: commonMaterialTestStrategy,//R20

    EXHIBITION_EXPENSE: commonMaterialTestStrategy,//R21
    FRANCHISE: commonMaterialTestStrategy,//R22
    FREIGHT_CHARGES: commonMaterialTestStrategy,//R23
    GAS_EXPENSES: commonMaterialTestStrategy,//R24
    HOTEL_EXPENSE: commonMaterialTestStrategy,//R25
    INSURANCE: commonMaterialTestStrategy,//R26
    LICENSE_FEE: commonMaterialTestStrategy,//R27
    LOCAL_TRAVELLING: commonMaterialTestStrategy,//R28
    MEDICAL_EXPENSES: commonMaterialTestStrategy,//R29
    MEMBERSHIP_FEE: commonMaterialTestStrategy,//R30

    MOTOR_VEHICLE_EXPENSES: commonMaterialTestStrategy,//R31
    MESSING_EXPENSES: commonMaterialTestStrategy,//R32
    OFFICE_EXPENSE: commonMaterialTestStrategy,//R33
    OVERSEAS_TRAVELLING: commonMaterialTestStrategy,//R34
    PENALTY: commonMaterialTestStrategy,//R35
    PRELIMINARY_EXPENSES: commonMaterialTestStrategy,//R37
    PRINTING_AND_STATIONERY: commonMaterialTestStrategy,//R38
    PROPERTY_AGENCY_FEES: commonMaterialTestStrategy,//R39
    REPAIR_AND_MAINTENANCE: commonMaterialTestStrategy,//R40

    SAMPLE_FEE: commonMaterialTestStrategy,//R41
    SECRETARIAL_FEE: commonMaterialTestStrategy,//R42
    STAFF_WELFARE: commonMaterialTestStrategy,//R43
    STORAGE_FEE: commonMaterialTestStrategy,//R44
    SUBSCRIPTION_FEE: commonMaterialTestStrategy,//R45
    SUBCONTRACTING_EXPENSES: commonMaterialTestStrategy,//R46
    SUNDRY_EXPENSE: commonMaterialTestStrategy,//R47
    TELEPHONE_AND_INTERNET: commonMaterialTestStrategy,//R48
    TRAINING_EXPENSE: commonMaterialTestStrategy,//R49
    TRAVELLING_EXPENSES: commonMaterialTestStrategy,//R50

    TRANSPORTATION: commonMaterialTestStrategy,//R51
    UTILITIES_EXPENSES: commonMaterialTestStrategy,//R52
    VIRTUAL_OFFICE: commonMaterialTestStrategy,//R53
    WAGES_EXPENSE: commonMaterialTestStrategy,//R54
    WEB_SERVICES_AND_EMAIL: commonMaterialTestStrategy,//R55
    INTEREST: commonMaterialTestStrategy,//R60
    FIANCE_BANK_CHARGES: commonMaterialTestStrategy,//R61

    // Expenses - RX session
    RENTAL_EXPENSE: rentalExpensesStrategy, //RX1
    DONATION: donationStrategy,
    COMMISSION_PAYMENT: commissionPaymentStrategy,
    CONSULTANCY_FEE: debtFeesTransactionStrategy,
    MANAGEMENT_FEE: debtFeesTransactionStrategy,
    SUBCONTRACTOR_CHARGES: debtFeesTransactionStrategy,
    BAD_DEBT: debtFeesTransactionStrategy,
    PROVISION_FOR_DOUBTFUL_DEBT: debtFeesTransactionStrategy,
    INTELLECTUAL_PROPERTY_PAYMENTS: commonTransactionStrategy,
    LEGAL_AND_PROFESSIONAL_FEE: debtFeesTransactionStrategy,

    // ======= Code-based keys (from paper-registry.ts) =======

    // Balance Sheet - Non-Current Assets (BS-NCA)
    'BS-NCA-PPE': propertyPlantEquipmentStrategy,
    'BS-NCA-IA': intangibleAssetsStrategy,
    // Same strategy instance as IA — AdjustTabs / mergeDataForAdjust details = { intangibleRows }
    'BS-NCA-ITL': InvestmentInTaxiCabsAndLicensesStrategy,
    'BS-NCA-ISUB': InvestmentSubsidiaryStrategy,
    'BS-NCA-IASS': InvestmentSubsidiaryStrategy,

    // Balance Sheet - Current Assets (BS-CA)
    'BS-CA-IOT': InvestmentSubsidiaryStrategy,
    'BS-CA-INV': inventoriesStrategy,
    'BS-CA-ORC': commonStrategy,
    'BS-CA-DPP': commonStrategy,
    'BS-CA-PPM': commonStrategy,
    'BS-CA-TR': commonStrategy,
    'BS-CA-DIMH': commonStrategy,
    'BS-CA-DIR': dueFromToDirectorsStrategy,
    'BS-CA-DSS': commonStrategy,
    'BS-CA-DRP': commonStrategy,
    'BS-CA-DRP2': commonStrategy,
    'BS-CA-DRP3': commonStrategy,
    'BS-CA-CASH': bankStrategy,
    'BS-CA-PND': commonStrategy,

    // Balance Sheet - Current Liabilities (BS-CL)
    'BS-CL-TP': commonStrategy,
    'BS-CL-DP': commonStrategy,
    'BS-CL-OP': commonStrategy,
    'BS-CL-DR': commonStrategy,
    'BS-CL-ACC': commonStrategy,
    'BS-CL-BOD': bankStrategy,
    'BS-CL-BLS': bankLoanStrategy,
    'BS-CL-OFL': vehicleLoanStrategy,
    'BS-CL-DTS': commonStrategy,
    'BS-CL-DTSH': commonStrategy,
    'BS-CL-DTRC': commonStrategy,
    'BS-CL-DRP': commonStrategy,
    'BS-CL-DRP2': commonStrategy,
    'BS-CL-DRP3': commonStrategy,
    'BS-CL-DTD': dueFromToDirectorsStrategy,
    'BS-CL-RND': commonStrategy,

    // Balance Sheet - Non-Current Liabilities (BS-NCL)
    'BS-NCL-ONI': commonStrategy,
    'BS-NCL-BLS': bankLoanStrategy,
    'BS-NCL-OFL': vehicleLoanStrategy,

    // Balance Sheet - Equity (BS-EQ)
    'BS-EQ-IC': basicStrategy,
    'BS-EQ-AL': basicStrategy,
    'BS-EQ-OFFS': commonStrategy,
    'BS-EQ-DIV': basicStrategy,

    // Profit & Loss - Turnover (PL-TURN)
    'PL-TURN-SI': salesIncomeStrategy,
    'PL-TURN-SE': salesIncomeStrategy,
    'PL-TURN-RI': rentalExpensesStrategy,

    // Profit & Loss - Cost of Turnover (PL-COT)
    'PL-COT-OI': inventoriesStrategy,
    'PL-COT-PUR': purchasesStrategy,
    'PL-COT-CI': inventoriesStrategy,

    // Profit & Loss - Other Income (PL-OI)
    'PL-OI-II': interestIncomeStrategy,
    'PL-OI-SFG': interestIncomeStrategy,
    'PL-OI-DI': commonTransactionStrategy,
    'PL-OI-EXG': gainLossExchangeStrategy,
    'PL-OI-ALI': commonTransactionStrategy,
    'PL-OI-GOD': gainLossDisposalStrategy,
    'PL-OI-SI': commonTransactionStrategy,

    // Profit & Loss - Administrative Expenses (PL-AE)
    'PL-AE-ACF': commonMaterialTestStrategy,
    'PL-AE-ADM': commonMaterialTestStrategy,
    'PL-AE-ADP': commonMaterialTestStrategy,
    'PL-AE-AR': commonMaterialTestStrategy,
    'PL-AE-AOP': commonMaterialTestStrategy,
    'PL-AE-BC': commonMaterialTestStrategy,
    'PL-AE-BMF': commonMaterialTestStrategy,
    'PL-AE-BRF': commonMaterialTestStrategy,
    'PL-AE-BD': debtFeesTransactionStrategy,
    'PL-AE-PDD': debtFeesTransactionStrategy,
    'PL-AE-CER': commonMaterialTestStrategy,
    'PL-AE-CLE': commonMaterialTestStrategy,
    'PL-AE-CMP': commonMaterialTestStrategy,
    'PL-AE-CSE': commonMaterialTestStrategy,
    'PL-AE-COM': commissionPaymentStrategy,
    'PL-AE-CF': debtFeesTransactionStrategy,
    'PL-AE-CONS': commonMaterialTestStrategy,
    'PL-AE-CUR': commonMaterialTestStrategy,
    'PL-AE-CRR': commonMaterialTestStrategy,
    'PL-AE-CUD': commonMaterialTestStrategy,
    'PL-AE-DSN': commonMaterialTestStrategy,
    'PL-AE-DEP': depreciationSharedStrategy,
    'PL-AE-AIA': intangibleAssetsStrategy,
    'PL-AE-DR': directorSalaryStrategy,
    'PL-AE-DQ': directorSalaryStrategy,
    'PL-AE-DON': donationStrategy,
    'PL-AE-EW': commonMaterialTestStrategy,
    'PL-AE-ENT': commonMaterialTestStrategy,
    'PL-AE-EXL': gainLossExchangeStrategy,
    'PL-AE-EXH': commonMaterialTestStrategy,
    'PL-AE-FRA': commonMaterialTestStrategy,
    'PL-AE-FRG': commonMaterialTestStrategy,
    'PL-AE-GAS': commonMaterialTestStrategy,
    'PL-AE-HOT': commonMaterialTestStrategy,
    'PL-AE-IML': commonStrategy,
    'PL-AE-INS': commonMaterialTestStrategy,
    'PL-AE-IPP': commonTransactionStrategy,
    'PL-AE-LPF': debtFeesTransactionStrategy,
    'PL-AE-LIC': commonMaterialTestStrategy,
    'PL-AE-LTR': commonMaterialTestStrategy,
    'PL-AE-LOD': gainLossDisposalStrategy,
    'PL-AE-MGT': debtFeesTransactionStrategy,
    'PL-AE-MED': commonMaterialTestStrategy,
    'PL-AE-MEM': commonMaterialTestStrategy,
    'PL-AE-MVE': commonMaterialTestStrategy,
    'PL-AE-MPF': mpfSummaryStrategy,
    'PL-AE-MES': commonMaterialTestStrategy,
    'PL-AE-OFF': commonMaterialTestStrategy,
    'PL-AE-OTR': commonMaterialTestStrategy,
    'PL-AE-PEN': commonMaterialTestStrategy,
    'PL-AE-PE': commonMaterialTestStrategy,
    'PL-AE-PS': commonMaterialTestStrategy,
    'PL-AE-PAF': commonMaterialTestStrategy,
    'PL-AE-REN': rentalExpensesStrategy,
    'PL-AE-RAM': commonMaterialTestStrategy,
    'PL-AE-SAL': salariesWageStrategy,
    'PL-AE-SAM': commonMaterialTestStrategy,
    'PL-AE-SCF': commonMaterialTestStrategy,
    'PL-AE-SWF': commonMaterialTestStrategy,
    'PL-AE-SD': commonMaterialTestStrategy,
    'PL-AE-STO': commonMaterialTestStrategy,
    'PL-AE-SUB': commonMaterialTestStrategy,
    'PL-AE-SUE': debtFeesTransactionStrategy,
    'PL-AE-SUN': commonMaterialTestStrategy,
    'PL-AE-TEL': commonMaterialTestStrategy,
    'PL-AE-TRN': commonMaterialTestStrategy,
    'PL-AE-TRV': commonMaterialTestStrategy,
    'PL-AE-TP': commonMaterialTestStrategy,
    'PL-AE-UTL': commonMaterialTestStrategy,
    'PL-AE-VO': commonMaterialTestStrategy,
    'PL-AE-WAG': salariesWageStrategy,
    'PL-AE-WSE': commonMaterialTestStrategy,
    'PL-AE-WHT': withholdingTaxStrategy,

    // Profit & Loss - Finance Cost (PL-FC)
    'PL-FC-LI': interestSummaryStrategy,
    'PL-FC-INT': commonMaterialTestStrategy,
    'PL-FC-BC': commonMaterialTestStrategy,

    // Tax
    'BS-CA-TAXR': basicStrategy,
    'BS-CL-TAXP': basicStrategy,
    'PL-TAX': basicStrategy,
};

/** Resolve payload strategy by registry key or FS code (exact, then uppercase). */
export function resolvePayloadStrategy(
    keys: Array<string | null | undefined>,
): PayloadStrategy | undefined {
    for (const raw of keys) {
        const k = String(raw || '').trim();
        if (!k || k.toUpperCase() === 'NONE') continue;
        const hit = strategyMap[k as AdjustDialogKey] || strategyMap[k.toUpperCase() as AdjustDialogKey];
        if (hit) return hit;
    }
    return undefined;
}
